/**
 * 어시스트 드로어 크기 손잡이(D2 결정 2·7).
 *
 * 드로어는 우하단 고정이라 성장 방향은 위·왼쪽이다 — 손잡이를 상변·좌변
 * 둘로 나누면 모서리에서 겹친다. **모서리 하나**가 두 축을 동시에 옮긴다.
 * narrow 에서는 같은 노드가 높이만 바꾼다(너비는 이미 포화라 조절할
 * 여지가 없고, 아무 일도 안 하는 손잡이는 두지 않는다).
 *
 * `<div onPointerDown>` 이 아니라 `role="separator"` 를 가진 `<button>` 인
 * 이유: 키보드만으로도 조절할 수 있어야 한다. 키 입력에는 pointerup 이
 * 없으므로 **매 입력마다 저장**한다.
 */

import { useRef } from 'react'
import type { AssistSize, SizeBounds } from '../lib/assist-size'

const STEP = 32
const STEP_FAST = 96

export function AssistResizeHandle({
  narrow,
  bounds,
  size,
  onPreview,
  onCommit,
}: {
  narrow: boolean
  bounds: SizeBounds
  size: AssistSize
  onPreview: (next: Partial<AssistSize>) => void
  onCommit: (next: Partial<AssistSize>) => void
}): React.ReactElement {
  /** 드래그 시작점 — 포인터 이동량을 크기 변화로 옮기는 기준. */
  const originRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null)

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
    const step = e.shiftKey ? STEP_FAST : STEP
    switch (e.key) {
      case 'ArrowUp':
        onCommit({ h: size.h + step })
        break
      case 'ArrowDown':
        onCommit({ h: size.h - step })
        break
      case 'ArrowLeft':
        if (narrow) return
        onCommit({ w: size.w + step })
        break
      case 'ArrowRight':
        if (narrow) return
        onCommit({ w: size.w - step })
        break
      case 'Home':
        onCommit(narrow ? { h: bounds.minH } : { w: bounds.minW, h: bounds.minH })
        break
      case 'End':
        onCommit(narrow ? { h: bounds.maxH } : { w: bounds.maxW, h: bounds.maxH })
        break
      default:
        return
    }
    e.preventDefault()
  }

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>): void => {
    originRef.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>): void => {
    const o = originRef.current
    if (o === null) return
    // 위·왼쪽으로 끌면 커진다(우하단 고정).
    const next: Partial<AssistSize> = { h: o.h + (o.y - e.clientY) }
    if (!narrow) next.w = o.w + (o.x - e.clientX)
    onPreview(next)
  }

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>): void => {
    const o = originRef.current
    if (o === null) return
    originRef.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    const next: Partial<AssistSize> = { h: o.h + (o.y - e.clientY) }
    if (!narrow) next.w = o.w + (o.x - e.clientX)
    onCommit(next)
  }

  return (
    <button
      type="button"
      role="separator"
      aria-orientation={narrow ? 'horizontal' : 'vertical'}
      aria-label="질문 창 크기 조절"
      aria-valuenow={Math.round(size.h)}
      aria-valuemin={Math.round(bounds.minH)}
      aria-valuemax={Math.round(bounds.maxH)}
      data-testid="assist-resize-handle"
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      style={{ touchAction: 'none' }}
      className={
        narrow
          ? 'absolute left-1/2 top-0 h-3 w-10 -translate-x-1/2 cursor-ns-resize rounded-b-[var(--radius-lg)] bg-[var(--color-border)]'
          : 'absolute left-0 top-0 h-4 w-4 cursor-nwse-resize rounded-tl-[var(--radius-lg)] bg-[var(--color-border)]'
      }
    />
  )
}
